/**
 * Classes API + TanStack Query hooks (Phase 4 — Calendar).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';

export type ClassResponse = components['schemas']['ClassResponse'];
export type ClassCreate = components['schemas']['ClassCreate'];
export type ClassUpdate = components['schemas']['ClassUpdate'];
export type Recurrence = components['schemas']['Recurrence'];
export type PaginatedClasses = components['schemas']['PaginatedResult_ClassResponse_'];

export interface ListClassesParams {
  status?: string;
  teacher_id?: string;
  subject_id?: string;
}

export const classKeys = {
  all: ['calendar', 'classes'] as const,
  list: (p: ListClassesParams) => ['calendar', 'classes', 'list', p] as const,
  detail: (id: string) => ['calendar', 'classes', 'detail', id] as const,
};

export async function listClasses(params: ListClassesParams = {}): Promise<PaginatedClasses> {
  return api.get<PaginatedClasses>('/v1/calendar/classes', {
    query: {
      status: params.status || undefined,
      teacher_id: params.teacher_id || undefined,
      subject_id: params.subject_id || undefined,
    },
  });
}

export async function createClass(body: ClassCreate): Promise<ClassResponse> {
  return api.post<ClassResponse>('/v1/calendar/classes', body);
}

export async function updateClass(id: string, body: ClassUpdate): Promise<ClassResponse> {
  return api.patch<ClassResponse>(`/v1/calendar/classes/${id}`, body);
}

export async function cancelClass(id: string, reason: string): Promise<ClassResponse> {
  return api.delete<ClassResponse>(`/v1/calendar/classes/${id}`, {
    query: { reason },
  });
}

export function useClasses(params: ListClassesParams = {}) {
  return useQuery({
    queryKey: classKeys.list(params),
    queryFn: () => listClasses(params),
    staleTime: 30_000,
  });
}

/** Single class — the session page's left rail + Edit-repeats modal. */
export function useClass(id: string | undefined) {
  return useQuery({
    queryKey: classKeys.detail(id ?? '__none__'),
    queryFn: () => api.get<ClassResponse>(`/v1/calendar/classes/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createClass,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classKeys.all });
      // Sessions list is downstream — invalidate it too.
      qc.invalidateQueries({ queryKey: ['calendar', 'sessions'] });
    },
  });
}

export function useUpdateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ClassUpdate }) => updateClass(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classKeys.all });
      qc.invalidateQueries({ queryKey: ['calendar', 'sessions'] });
    },
  });
}

export function useCancelClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelClass(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classKeys.all });
      qc.invalidateQueries({ queryKey: ['calendar', 'sessions'] });
    },
  });
}

